// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */

import { Box, Image, Theme, Typography } from "@outschool/backpack";
import { LandingPageHeroSectionCsrFragment } from "@outschool/gql-frontend-generated";
import { gql } from "@outschool/ui-apollo";
import { TrustpilotMicroCombo } from "@outschool/ui-components-website";
import { useIsMobile } from "@outschool/ui-utils";
import React from "react";

import { MAX_WIDTH } from "./constants";
import LandingPageCtaButton from "./LandingPageCtaButton";

const contentfulImageUrl = (url: string, width: number) =>
  `${url}?fm=webp&q=70&w=${width}`;

export const CONTENTFUL_LANDING_PAGE_HERO_FRAGMENT = gql`
  fragment LandingPageHeroSectionCSR on ContentfulLandingPageHeroSection {
    title
    subtitle
    shouldShowCta
    buttonCtaText
    buttonUrl
    buttonUrlLoggedIn
    shouldShowTrustPilot
    animationUrl
    backgroundImage {
      url
    }
    mainImage {
      url
    }
  }
`;
const IFRAME_WIDTH_MOBILE = 384;

export default function LandingPageHeroSection(
  props: LandingPageHeroSectionCsrFragment
) {
  const isMobile = useIsMobile();

  const {
    title,
    subtitle,
    shouldShowCta,
    buttonCtaText,
    buttonUrl,
    buttonUrlLoggedIn,
    shouldShowTrustPilot,
    mainImage,
    backgroundImage,
    animationUrl
  } = props;

  const canShowCta = !!(
    shouldShowCta &&
    buttonCtaText &&
    buttonUrl &&
    buttonUrlLoggedIn
  );

  return (
    <Box
      sx={(theme: Theme) => ({
        ...(backgroundImage?.url
          ? {
              background: `url(${backgroundImage.url}),${
                theme.gradients?.primarySecondary || "transparent"
              } `,
              backgroundSize: "40% auto, 100% auto",
              backgroundPosition: "bottom right, center",
              backgroundRepeat: "no-repeat, no-repeat"
            }
          : { background: theme.gradients?.primarySecondary || "transparent" }),
        borderRadius: "1.5rem",
        width: "100%",
        maxWidth: MAX_WIDTH,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2.5rem 3.5rem",
        position: "relative",
        minHeight: "400px",
        maxHeight: "50rem",

        [theme.breakpoints.down("lg")]: {
          minHeight: "242px",
          padding: theme.typography.pxToRem(10),
          overflow: "hidden",
          margin: "0 auto",
          width: "95%"
        }
      })}
    >
      <Box
        sx={(theme: Theme) => ({
          display: "flex",
          zIndex: 1,
          alignItems: "center",
          justifyContent: "center",
          height: "auto",
          width: "auto",

          [theme.breakpoints.down("lg")]: {
            height: "100%",
            width: "100%",
            flexDirection: "column-reverse"
          }
        })}
      >
        <Box
          sx={(theme: Theme) => ({
            width: "50%",
            height: "100%",
            paddingRight: "1rem",
            [theme.breakpoints.down("lg")]: {
              marginTop: "1.5rem",
              width: "100%"
            }
          })}
        >
          <Typography
            as={Box}
            variant="h1"
            sx={(theme: Theme) => ({
              fontSize: "3.75em !important",
              fontWeight: 700,
              fontFamily: "Ginto Nord",
              [theme.breakpoints.down("lg")]: {
                fontSize: `${theme.typography.pxToRem(24)} !important`
              }
            })}
          >
            {title}
          </Typography>
          <Typography
            as={Box}
            variant="h5"
            sx={(theme: Theme) => ({
              color: "#444",
              fontSize: "22px",
              lineHeight: "125%",
              fontWeight: "500",
              marginTop: ".5rem",
              [theme.breakpoints.down("lg")]: {
                fontSize: `${theme.typography.pxToRem(16)}`
              }
            })}
          >
            {subtitle}
          </Typography>
          <Box
            sx={(theme: Theme) => ({
              display: "flex",
              marginTop: "3rem",
              alignItems: "center",
              gap: "1.5rem",
              [theme.breakpoints.down("lg")]: {
                justifyContent: "center"
              }
            })}
          >
            {canShowCta && (
              <LandingPageCtaButton
                pageSection="HeroSection"
                ctaText={buttonCtaText}
                ctaUrl={buttonUrl}
                ctaUrlLoggedIn={buttonUrlLoggedIn}
                sx={{ justifyContent: "center" }}
              />
            )}
            {shouldShowTrustPilot && !isMobile && (
              <TrustpilotMicroCombo
                sx={{
                  height: "42px",
                  marginLeft: "-8px !important"
                }}
                widgetCss={{ width: "200px" }}
              />
            )}
          </Box>
        </Box>

        {!!mainImage?.url && (
          <Box
            sx={{
              flex: 1
            }}
          >
            <Image
              loading="eager"
              priority
              alt="Landing page header image"
              src={contentfulImageUrl(mainImage.url, 600)}
              sx={(theme: Theme) => ({
                height: "400px",
                width: "600px",
                borderBottomRightRadius: "1.5rem",
                [theme.breakpoints.down("lg")]: {
                  width: "300px",
                  height: "200px"
                }
              })}
            />
          </Box>
        )}

        {animationUrl && !mainImage?.url && (
          <Box
            sx={(theme: Theme) => ({
              position: "relative",
              overflow: "visible",
              width: "50%",

              [theme.breakpoints.down("lg")]: {
                overflow: "visible",
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 2
              }
            })}
          >
            {isMobile ? (
              <iframe
                src={animationUrl}
                title="Landing Page Header Animation"
                loading="eager"
                scrolling="no"
                style={{
                  border: "none",
                  background: "transparent !important",
                  height: "100%",
                  minHeight: "210px",
                  width: IFRAME_WIDTH_MOBILE,
                  maxWidth: IFRAME_WIDTH_MOBILE
                }}
              />
            ) : (
              <iframe
                src={animationUrl}
                title="Landing Page Header Animation"
                loading="eager"
                width={"828px"}
                height={"505px"}
                scrolling="no"
                style={{
                  position: "absolute",
                  top: "-200px",
                  border: "none",
                  background: "transparent !important",
                  transformOrigin: "left center"
                }}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
